/**
 * HUD: minimap, money / wanted / weapon stack, speedometer, prompts, notifications,
 * subtitles, big screens (WASTED / BUSTED / MISSION PASSED / FAILED), start screen,
 * pause menu and end-game stats. Pure DOM + one Canvas 2D minimap, zero assets.
 *
 * DOM writes happen only when the displayed value changes; the minimap redraws each
 * frame (every other frame on mobile).
 */
import { G } from './ctx';
import { clamp, formatMoney, formatTime } from './util';
import { HUD_CSS } from './hud_styles';
import { KEY, SPEEDO_SVG, STAR_SVG, WEAPON_ICONS } from './hud_icons';
import { Minimap } from './hud_minimap';
import { PauseMenu, createStartScreen, createStatsScreen, hasSave } from './hud_menus';
import type { IHUD, IVehicle, MapBlip, WeaponId } from './types';

const ARC_LEN = 235.6;
const CHEAT_FADE_AT = 36;
const CHEAT_GONE_AT = 40;

const VEHICLE_NAMES: Record<string, string> = {
  sedan: 'Sedan',
  hatch: 'Hatchback',
  sports: 'Sports Car',
  suv: 'SUV',
  taxi: 'Taxi',
  van: 'Van',
  truck: 'Truck',
  bus: 'Bus',
  police: 'Police Cruiser',
};

const SPEAKER_COLORS: Record<string, string> = {
  zoli: '#ff9a3c',
  reka: '#ff5fa8',
  réka: '#ff5fa8',
  you: '#8fd3ff',
  player: '#8fd3ff',
  cop: '#4f9cf5',
  police: '#4f9cf5',
  dispatch: '#4f9cf5',
  radio: '#c9b6ff',
};

function vehicleName(v: IVehicle): string {
  const t = v.spec?.type ?? v.type;
  return VEHICLE_NAMES[t] ?? t.charAt(0).toUpperCase() + t.slice(1);
}

function speakerColor(name: string): string {
  const k = name.trim().toLowerCase();
  if (SPEAKER_COLORS[k]) return SPEAKER_COLORS[k];
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 85% 68%)`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

interface WeaponExtras {
  clip?: number;
  reloading?: boolean | number;
}

export class HUD implements IHUD {
  paused = false;
  settings: IHUD['settings'] = { quality: 'high', bloom: true, shadows: true, invertY: false };

  private root: HTMLElement;
  private map: Minimap;
  private gameCanvas: HTMLElement | null;
  private pauseMenu: PauseMenu | null = null;

  // element refs
  private elMoney: HTMLElement;
  private elStarsWrap: HTMLElement;
  private elStars: HTMLElement[];
  private elWName: HTMLElement;
  private elWAmmo: HTMLElement;
  private elWIcon: HTMLElement;
  private elReload: HTMLElement;
  private elReloadBar: HTMLElement;
  private elClock: HTMLElement;
  private elFps: HTMLElement;
  private elHp: HTMLElement;
  private elAr: HTMLElement;
  private elHeat: HTMLElement;
  private elHeatBar: HTMLElement;
  private elObj: HTMLElement;
  private elTimer: HTMLElement;
  private elBr: HTMLElement;
  private elArc: SVGPathElement;
  private elKmh: HTMLElement;
  private elVName: HTMLElement;
  private elVRadio: HTMLElement;
  private elToasts: HTMLElement;
  private elPrompt: HTMLElement;
  private elCheat: HTMLElement | null = null;
  private elCross: HTMLElement;
  private elSub: HTMLElement;
  private elSubSpk: HTMLElement;
  private elSubTxt: HTMLElement;
  private elRFlash: HTMLElement;
  private elRFlashName: HTMLElement;
  private elBig: HTMLElement;
  private elVig: HTMLElement;
  private elLow: HTMLElement;

  // shown-value caches (avoid DOM writes)
  private moneyShown = 0;
  private moneyStr = '';
  private starsLevel = -1;
  private starsBlink = false;
  private heatOn = false;
  private heatShown = -1;
  private weaponId: WeaponId | '' = '';
  private ammoStr = '';
  private ammoLow = false;
  private reloadMode = 0; // 0 off, 1 animated, 2 progress
  private reloadShown = -1;
  private clockStr = '';
  private fpsShown = -1;
  private hpShown = -1;
  private arShown = -1;
  private speedoOn = false;
  private kmhShown = -1;
  private arcShown = -1;
  private vNameStr = '';
  private vRadioStr = '';
  private promptStr = '';
  private crossOn = false;
  private lowOn = false;
  private objText = '';
  private timerStr = '';
  private timerUrgent = false;
  private cheatFaded = false;
  private touchApplied = false;
  private firstUpdate = true;
  private frame = 0;
  private mapDt = 0;
  private enteredAt = -1e9;
  private markerNear = false;

  // timers
  private subTimer = 0;
  private rflashTimer = 0;
  private bigTimer = 0;
  private vigTimer = 0;
  private moneyFlashTimer = 0;

  constructor(container: HTMLElement) {
    if (!document.getElementById('hud-style')) {
      const st = document.createElement('style');
      st.id = 'hud-style';
      st.textContent = HUD_CSS;
      document.head.appendChild(st);
    }
    const root = document.createElement('div');
    root.id = 'hud';
    if (G.isMobile) root.classList.add('mobile');
    root.innerHTML = `
      <div class="h-tl"><div class="h-toasts"></div><div class="h-prompt sh"></div></div>
      <div class="h-tr">
        <div class="h-money">$0</div>
        <div class="h-stars">${STAR_SVG.repeat(5)}</div>
        <div class="h-weapon">
          <div class="h-wtext"><div class="h-wname sh"></div><div class="h-wammo sh"></div><div class="h-reload"><i></i></div></div>
          <div class="h-wicon"></div>
        </div>
        <div class="h-meta"><span class="h-clock sh">00:00</span><span class="h-fps">60 FPS</span></div>
      </div>
      <div class="h-bl">
        <div class="h-map"></div>
        <div class="h-bars"><div class="h-bar hp"><i></i></div><div class="h-bar ar"><i></i></div><div class="h-bar heat"><i></i></div></div>
        <div class="h-obj sh"></div>
        <div class="h-timer sh"></div>
      </div>
      <div class="h-br">
        <div class="h-speedo">${SPEEDO_SVG}<div class="h-kmh"><b>0</b><small>KM/H</small></div></div>
        <div class="h-vname sh"></div>
        <div class="h-vradio sh"></div>
      </div>
      <div class="h-cross"><i class="c"></i><i class="n"></i><i class="s"></i><i class="w"></i><i class="e"></i></div>
      <div class="h-sub"><span class="spk"></span><span class="txt"></span></div>
      <div class="h-rflash sh"><small>RADIO</small><span></span></div>
      <div class="h-vig"></div>
      <div class="h-low"></div>
      <div class="h-big"></div>`;
    const q = <T extends Element = HTMLElement>(sel: string): T => root.querySelector(sel) as T;
    this.elMoney = q('.h-money');
    this.elStarsWrap = q('.h-stars');
    this.elStars = Array.from(root.querySelectorAll<HTMLElement>('.h-star'));
    this.elWName = q('.h-wname');
    this.elWAmmo = q('.h-wammo');
    this.elWIcon = q('.h-wicon');
    this.elReload = q('.h-reload');
    this.elReloadBar = q('.h-reload i');
    this.elClock = q('.h-clock');
    this.elFps = q('.h-fps');
    this.elHp = q('.h-bar.hp i');
    this.elAr = q('.h-bar.ar i');
    this.elHeat = q('.h-bar.heat');
    this.elHeatBar = q('.h-bar.heat i');
    this.elObj = q('.h-obj');
    this.elTimer = q('.h-timer');
    this.elBr = q('.h-br');
    this.elArc = q<SVGPathElement>('.arc-fg');
    this.elKmh = q('.h-kmh b');
    this.elVName = q('.h-vname');
    this.elVRadio = q('.h-vradio');
    this.elToasts = q('.h-toasts');
    this.elPrompt = q('.h-prompt');
    this.elCross = q('.h-cross');
    this.elSub = q('.h-sub');
    this.elSubSpk = q('.h-sub .spk');
    this.elSubTxt = q('.h-sub .txt');
    this.elRFlash = q('.h-rflash');
    this.elRFlashName = q('.h-rflash span');
    this.elBig = q('.h-big');
    this.elVig = q('.h-vig');
    this.elLow = q('.h-low');

    this.map = new Minimap(this.mapSize());
    q('.h-map').appendChild(this.map.el);
    this.elAr.style.transform = 'scaleX(0)';
    this.elHeatBar.style.transform = 'scaleX(0)';

    this.root = root;
    container.appendChild(root);
    this.gameCanvas = document.getElementById('game');
    if (this.gameCanvas) this.gameCanvas.style.transition = 'filter 1.6s ease-out';

    G.events.on('moneyChanged', () => this.flashMoney());
    G.events.on('vehicleEntered', () => (this.enteredAt = G.time.t));
    window.addEventListener('resize', () => this.map.resize(this.mapSize()));
  }

  private mapSize(): number {
    return this.root?.classList.contains('touch') || G.isMobile ? 140 : 200;
  }

  // ------------------------------------------------------------------ update --

  update(dt: number): void {
    if (this.paused) {
      this.pauseMenu?.tick();
      return;
    }
    const p = G.player;
    if (!p) return;
    this.frame++;
    if (!this.touchApplied && G.input.touch) this.applyTouchLayout();

    // money (counts toward the target, flashes on change)
    const money = G.stats.money;
    if (this.firstUpdate) this.moneyShown = money;
    if (this.moneyShown !== money) {
      const diff = money - this.moneyShown;
      if (Math.abs(diff) < 1) this.moneyShown = money;
      else {
        const step = diff * Math.min(1, dt * 5);
        this.moneyShown += Math.abs(step) < 1 ? Math.sign(diff) : step;
      }
    }
    const ms = formatMoney(this.moneyShown);
    if (ms !== this.moneyStr) {
      this.moneyStr = ms;
      this.elMoney.textContent = ms;
    }

    // wanted stars + heat
    const w = G.police.wanted;
    const lvl = clamp(w.level | 0, 0, 5);
    if (lvl !== this.starsLevel) {
      this.starsLevel = lvl;
      for (let i = 0; i < 5; i++) this.elStars[i].classList.toggle('lit', i < lvl);
    }
    const blink = lvl > 0 && !!w.inSight;
    if (blink !== this.starsBlink) {
      this.starsBlink = blink;
      this.elStarsWrap.classList.toggle('blink', blink);
    }
    const heatOn = lvl > 0;
    if (heatOn !== this.heatOn) {
      this.heatOn = heatOn;
      this.elHeat.classList.toggle('on', heatOn);
    }
    if (heatOn) {
      const h = clamp(w.heat, 0, 1);
      if (Math.abs(h - this.heatShown) > 0.01) {
        this.heatShown = h;
        this.elHeatBar.style.transform = `scaleX(${h.toFixed(3)})`;
      }
    }

    // weapon panel
    const ws = G.weapons;
    const wid = ws.current;
    const def = ws.defs[wid];
    if (wid !== this.weaponId) {
      this.weaponId = wid;
      this.elWIcon.innerHTML = WEAPON_ICONS[wid] ?? '';
      this.elWName.textContent = def?.name ?? wid;
    }
    let ammoStr = '';
    let low = false;
    if (def && def.clip > 0) {
      const total = ws.ammo[wid];
      const clip = (ws as unknown as WeaponExtras).clip;
      if (typeof clip === 'number' && Number.isFinite(clip)) {
        ammoStr = `${clip} <span>/ ${Math.max(0, Math.round(total - clip))}</span>`;
        low = clip === 0 || total <= 0;
      } else if (Number.isFinite(total)) {
        ammoStr = String(Math.round(total));
        low = total <= 0;
      }
    }
    if (ammoStr !== this.ammoStr) {
      this.ammoStr = ammoStr;
      this.elWAmmo.innerHTML = ammoStr;
    }
    if (low !== this.ammoLow) {
      this.ammoLow = low;
      this.elWAmmo.classList.toggle('low', low);
    }
    const rl = (ws as unknown as WeaponExtras).reloading;
    const mode = typeof rl === 'number' ? (rl > 0 ? 2 : 0) : rl ? 1 : 0;
    if (mode !== this.reloadMode) {
      this.reloadMode = mode;
      this.elReload.classList.toggle('on', mode !== 0);
      this.elReload.classList.toggle('anim', mode === 1);
      if (mode !== 2) this.elReloadBar.style.transform = '';
      this.reloadShown = -1;
    }
    if (mode === 2) {
      const v = clamp(rl as number, 0, 1);
      if (Math.abs(v - this.reloadShown) > 0.01) {
        this.reloadShown = v;
        this.elReloadBar.style.transform = `scaleX(${v.toFixed(3)})`;
      }
    }

    // clock + fps
    const day = G.env.dayTime;
    const hh = Math.floor(day) % 24;
    const mm = Math.floor((day - Math.floor(day)) * 60);
    const cs = `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    if (cs !== this.clockStr) {
      this.clockStr = cs;
      this.elClock.textContent = cs;
    }
    const fps = Math.round(G.time.fps);
    if (fps !== this.fpsShown) {
      this.fpsShown = fps;
      this.elFps.textContent = fps + ' FPS';
    }

    // health / armor bars
    const hp = clamp(p.health / 100, 0, 1);
    if (Math.abs(hp - this.hpShown) > 0.004) {
      this.hpShown = hp;
      this.elHp.style.transform = `scaleX(${hp.toFixed(3)})`;
    }
    const ar = clamp(p.armor / 100, 0, 1);
    if (Math.abs(ar - this.arShown) > 0.004) {
      this.arShown = ar;
      this.elAr.style.transform = `scaleX(${ar.toFixed(3)})`;
    }
    const lowHp = p.health < 25 && !p.dead;
    if (lowHp !== this.lowOn) {
      this.lowOn = lowHp;
      this.elLow.classList.toggle('on', lowHp);
    }

    // speedometer
    const v = p.vehicle;
    const on = !!v;
    if (on !== this.speedoOn) {
      this.speedoOn = on;
      this.elBr.classList.toggle('on', on);
    }
    if (v) {
      const kmh = Math.round(Math.abs(v.speed) * 3.6);
      if (kmh !== this.kmhShown) {
        this.kmhShown = kmh;
        this.elKmh.textContent = String(kmh);
      }
      const pct = Math.round(clamp(Math.abs(v.speed) / Math.max(1, v.spec.maxSpeed), 0, 1) * 100);
      if (pct !== this.arcShown) {
        this.arcShown = pct;
        this.elArc.style.strokeDashoffset = (ARC_LEN * (1 - pct / 100)).toFixed(1);
      }
      const vn = vehicleName(v);
      if (vn !== this.vNameStr) {
        this.vNameStr = vn;
        this.elVName.textContent = vn;
      }
      const rn = G.audio?.station?.name ?? '';
      if (rn !== this.vRadioStr) {
        this.vRadioStr = rn;
        this.elVRadio.textContent = rn;
      }
    }

    // crosshair
    const cross = !!p.aiming && !p.dead;
    if (cross !== this.crossOn) {
      this.crossOn = cross;
      this.elCross.classList.toggle('on', cross);
    }

    // interaction prompt (throttled)
    if ((this.frame & 3) === 0) this.updatePrompt();

    // controls cheat sheet (fresh game, desktop)
    if (this.firstUpdate && !G.input.touch && !hasSave()) this.buildCheat();
    if (this.elCheat) {
      const t = G.time.t;
      if (t > CHEAT_GONE_AT) {
        this.elCheat.remove();
        this.elCheat = null;
      } else if (t > CHEAT_FADE_AT && !this.cheatFaded) {
        this.cheatFaded = true;
        this.elCheat.classList.add('fade');
      }
    }

    // minimap (every frame; every other frame on mobile)
    this.mapDt += dt;
    if (!G.isMobile || (this.frame & 1) === 0) {
      this.map.draw(this.mapDt);
      this.mapDt = 0;
    }
    this.firstUpdate = false;
  }

  private applyTouchLayout(): void {
    this.touchApplied = true;
    this.root.classList.add('touch');
    this.map.resize(this.mapSize());
    if (this.elCheat) {
      this.elCheat.remove();
      this.elCheat = null;
    }
  }

  private updatePrompt(): void {
    const p = G.player;
    let str = '';
    let markerNear = false;
    if (!p.dead && !G.controlsLocked) {
      if (p.vehicle) {
        if (G.time.t - this.enteredAt < 5) str = `${KEY('E')}<span>Exit vehicle</span>`;
      } else {
        const nv = G.entities.nearestVehicle(p.pos.x, p.pos.z, 3.5, (x) => !x.destroyed);
        if (nv) str = `${KEY('E')}<span>Enter ${escapeHtml(vehicleName(nv))}</span>`;
        else {
          const blips = this.map.blips;
          for (let i = 0; i < blips.length; i++) {
            const b = blips[i];
            if (b.kind !== 'marker') continue;
            const dx = b.x - p.pos.x;
            const dz = b.z - p.pos.z;
            if (dx * dx + dz * dz < 14 * 14) {
              markerNear = true;
              str = `<span>Walk into the marker to start the mission</span>`;
              break;
            }
          }
        }
      }
    }
    this.markerNear = markerNear;
    if (str !== this.promptStr) {
      this.promptStr = str;
      this.elPrompt.innerHTML = str;
      this.elPrompt.classList.toggle('on', !!str);
    }
  }

  private buildCheat(): void {
    const c = document.createElement('div');
    c.className = 'h-cheat';
    const rows: [string, string][] = [
      ['WASD', 'Move'],
      ['Mouse', 'Look'],
      ['Shift', 'Sprint'],
      ['Space', 'Jump / Handbrake'],
      ['LMB', 'Fire'],
      ['RMB', 'Aim'],
      ['E', 'Enter / exit'],
      ['Scroll', 'Switch weapon'],
      ['R', 'Radio'],
      ['V', 'Camera'],
      ['H', 'Horn'],
      ['Esc', 'Pause'],
    ];
    c.innerHTML = rows.map(([k, t]) => `<div><span>${t}</span>${KEY(k)}</div>`).join('');
    this.elCheat = c;
    this.root.querySelector('.h-tl')?.appendChild(c);
  }

  private flashMoney(): void {
    this.elMoney.classList.add('flash');
    clearTimeout(this.moneyFlashTimer);
    this.moneyFlashTimer = window.setTimeout(() => this.elMoney.classList.remove('flash'), 380);
  }

  // ---------------------------------------------------------------- toasts --

  notify(text: string, seconds = 4): void {
    const t = document.createElement('div');
    t.className = 'h-toast';
    t.textContent = text;
    this.elToasts.appendChild(t);
    while (this.elToasts.children.length > 4) this.elToasts.firstElementChild?.remove();
    window.setTimeout(() => t.classList.add('in'), 20);
    window.setTimeout(
      () => {
        t.classList.add('out');
        window.setTimeout(() => t.remove(), 320);
      },
      Math.max(0.5, seconds) * 1000,
    );
  }

  subtitle(speaker: string, text: string, seconds?: number): void {
    this.elSubSpk.textContent = speaker ? speaker + ': ' : '';
    this.elSubSpk.style.color = speakerColor(speaker || 'x');
    this.elSubTxt.textContent = text;
    this.elSub.classList.add('on');
    clearTimeout(this.subTimer);
    const dur = seconds ?? clamp(2.2 + text.length * 0.055, 3, 9);
    this.subTimer = window.setTimeout(() => this.elSub.classList.remove('on'), dur * 1000);
  }

  setObjective(text: string): void {
    if (text === this.objText) return;
    this.objText = text;
    this.elObj.textContent = text;
    this.elObj.classList.toggle('on', !!text);
  }

  setTimer(seconds: number | null): void {
    if (seconds === null) {
      if (this.timerStr !== '') {
        this.timerStr = '';
        this.elTimer.classList.remove('on', 'urgent');
        this.timerUrgent = false;
      }
      return;
    }
    const s = formatTime(Math.max(0, Math.ceil(seconds)));
    if (s !== this.timerStr) {
      if (this.timerStr === '') this.elTimer.classList.add('on');
      this.timerStr = s;
      this.elTimer.textContent = s;
    }
    const urgent = seconds <= 10;
    if (urgent !== this.timerUrgent) {
      this.timerUrgent = urgent;
      this.elTimer.classList.toggle('urgent', urgent);
    }
  }

  // ------------------------------------------------------------- big text --

  private big(kind: string, title: string, opts: { pre?: string; sub?: string; reward?: string; fast?: boolean; autoHide?: number } = {}): void {
    clearTimeout(this.bigTimer);
    this.elBig.className = 'h-big on ' + kind;
    this.elBig.innerHTML =
      `<div class="h-big-shade"></div><div class="h-big-wrap${opts.fast ? ' fast' : ''}">` +
      (opts.pre ? `<div class="h-big-pre">${escapeHtml(opts.pre)}</div>` : '') +
      `<div class="h-big-title">${escapeHtml(title)}</div>` +
      (opts.sub ? `<div class="h-big-line"></div><div class="h-big-sub">${escapeHtml(opts.sub)}</div>` : '') +
      (opts.reward ? `<div class="h-big-reward">${escapeHtml(opts.reward)}</div>` : '') +
      `</div>`;
    if (opts.autoHide) this.bigTimer = window.setTimeout(() => this.hideBigText(), opts.autoHide * 1000);
  }

  showWasted(): void {
    this.big('wasted', 'WASTED');
    this.setCanvasFilter('grayscale(1) brightness(.55) contrast(1.1)');
  }

  showBusted(): void {
    this.big('busted', 'BUSTED');
    this.setCanvasFilter('grayscale(.75) brightness(.6) sepia(.15)');
  }

  showMissionPassed(name: string, reward: number): void {
    this.big('passed', 'MISSION PASSED', { sub: name, reward: reward > 0 ? '+' + formatMoney(reward) : undefined, fast: true, autoHide: 5.5 });
  }

  showMissionFailed(reason: string): void {
    this.big('failed', 'MISSION FAILED', { sub: reason, fast: true, autoHide: 5 });
  }

  hideBigText(): void {
    clearTimeout(this.bigTimer);
    this.elBig.className = 'h-big';
    this.elBig.innerHTML = '';
    this.setCanvasFilter('');
  }

  private setCanvasFilter(f: string): void {
    if (this.gameCanvas && this.gameCanvas.style.filter !== f) this.gameCanvas.style.filter = f;
  }

  showRadio(name: string): void {
    this.elRFlashName.textContent = name;
    this.elRFlash.classList.add('on');
    clearTimeout(this.rflashTimer);
    this.rflashTimer = window.setTimeout(() => this.elRFlash.classList.remove('on'), 2000);
  }

  damageFlash(): void {
    this.elVig.classList.add('hit');
    clearTimeout(this.vigTimer);
    this.vigTimer = window.setTimeout(() => this.elVig.classList.remove('hit'), 90);
  }

  setBlips(blips: MapBlip[]): void {
    this.map.blips = blips;
  }

  /** current mission blips (read by the prompt logic; exposed for missions/debug) */
  get blips(): MapBlip[] {
    return this.map.blips;
  }

  /** true while the player stands near a mission marker blip */
  get nearMarker(): boolean {
    return this.markerNear;
  }

  // ---------------------------------------------------------------- screens --

  showStartScreen(): Promise<void> {
    if (G.input?.touch) this.applyTouchLayout();
    const { el, done } = createStartScreen(this.settings, G.isMobile, !!G.input?.touch);
    this.root.classList.add('menu');
    this.root.appendChild(el);
    return done.then(() => this.root.classList.remove('menu'));
  }

  togglePause(): void {
    if (this.paused) this.resume();
    else {
      this.paused = true;
      if (!this.pauseMenu) {
        this.pauseMenu = new PauseMenu(this.settings, () => this.resume());
        this.root.appendChild(this.pauseMenu.el);
      }
      this.pauseMenu.open();
    }
  }

  /** leave the pause menu (also used by the Resume buttons) */
  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.pauseMenu?.close();
    if (!G.isMobile && !G.input.touch) G.inputCtl?.requestPointerLock();
  }

  showStats(stats: Record<string, string | number>): void {
    const wasPaused = this.paused;
    this.paused = true;
    this.pauseMenu?.close();
    const el = createStatsScreen(stats, () => {
      this.paused = wasPaused;
      if (wasPaused) this.pauseMenu?.open();
      else if (!G.isMobile && !G.input.touch) G.inputCtl?.requestPointerLock();
    });
    this.root.appendChild(el);
  }
}
